/* ══════════════════════════════════════════════════════════════════════════
   KEYSTONE JACKET — EARLY ACCESS
   Every interactive part of the page, in one file. Vanilla, no framework,
   no dependencies, safe to load anywhere in the document.

   Design rules this file follows:

   1. PROGRESSIVE ENHANCEMENT, NOT REPLACEMENT.
      The size chart and the fabric calculator both render as real, complete
      HTML tables in the markup. This script hides the table and reveals the
      interactive widget. With JS off — or blocked, which happens inside some
      in-app browsers — every number on the page is still readable. That is a
      hard requirement here: this page's whole job is getting somebody to a
      fabric shop with the right figure.

   2. ONE SOURCE OF TRUTH FOR THE DATES.
      The countdown, the .ics file and the Google Calendar link all read the
      same two ISO datetimes off the section's data attributes, which come
      from theme-editor settings. Nothing is hardcoded, so moving the launch
      is one field in the theme editor, not a code change.

   3. NOTHING THROWS.
      Each block returns early if its element is absent. A section Jack
      removes in the editor cannot break the rest of the page.
   ══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* This file is loaded from theme.liquid <head> for the early-access
     template, and again from ksea-notice as a fallback in case the page ends
     up on a template with a different suffix. A browser executes the same
     src twice quite happily, which would double every listener and append the
     size pills twice — so the second run stops here. */
  if (window.__KSEA_LOADED__) return;
  window.__KSEA_LOADED__ = true;

  /* ── helpers ─────────────────────────────────────────────────────────── */

  var $  = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  /* Trim a trailing .0 so 13.0 reads as "13" and 8.5 stays "8.5". */
  function num(v) { return String(Math.round(v * 10) / 10).replace(/\.0$/, ''); }

  /* Parse an ISO datetime that carries an explicit offset, e.g.
     2026-09-16T18:00:00+01:00. Date's ISO parsing is reliable for this shape
     in every browser we care about. Anything unparseable returns null so the
     caller can fall back rather than render "NaN".  */
  function parseISO(s) {
    if (!s) return null;
    var d = new Date(String(s).trim());
    return isNaN(d.getTime()) ? null : d;
  }

  /* ══ 1. INSTAGRAM IN-APP BROWSER NOTICE ═══════════════════════════════
     Instagram's webview mangles or silently drops blob downloads, which is
     exactly how the .ics reminder — the page's primary conversion — fails.
     The bar is the warning. It is sticky rather than modal on purpose: a
     modal on entry loses people who have just tapped through from a DM.

     Dismissal is deliberately NOT persisted. It closes for this view and
     comes back on reload, because the cost of missing it is a broken
     reminder button and the cost of seeing it twice is nothing. */

  function initNotice() {
    var bar = $('[data-ksea-notice]');
    if (!bar) return;

    var inApp = /Instagram|FBAN|FBAV|FB_IAB|FBIOS|Line\/|MicroMessenger/i.test(navigator.userAgent || '');
    var always = bar.getAttribute('data-always') === 'true';

    /* The bar is position:fixed, so the page has to be told how much room to
       leave for it. Its height is not a constant — the notice wraps to two or
       three lines depending on viewport width, and the copy is editable in
       the theme editor — so it is measured, not assumed, and re-measured on
       resize and rotate. */
    function measure() {
      var h = bar.hidden ? 0 : bar.offsetHeight;
      document.documentElement.style.setProperty('--ksea-notice-h', h + 'px');
    }

    if (inApp || always) {
      bar.hidden = false;
      bar.setAttribute('data-detected', inApp ? 'true' : 'false');
      document.documentElement.classList.add(inApp ? 'ksea-inapp' : 'ksea-notice-shown');
      measure();
      if (window.ResizeObserver) {
        try { new ResizeObserver(measure).observe(bar); } catch (e) {}
      }
      window.addEventListener('resize', measure, { passive: true });
      window.addEventListener('orientationchange', function () { setTimeout(measure, 250); });
    }

    /* Retract on scroll down, return on scroll up — see the note in the
       stylesheet. The 140px threshold keeps it still while the page settles
       and while somebody nudges the hero around; the 6px delta stops it
       flickering on the tiny scroll jitter a thumb produces on iOS. */
    if (!bar.hidden) {
      var lastY = window.pageYOffset || 0;
      var ticking = false;
      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          var y = window.pageYOffset || 0;
          var d = y - lastY;
          if (Math.abs(d) < 6) return;
          if (d > 0 && y > 140) bar.classList.add('is-away');
          else if (d < 0) bar.classList.remove('is-away');
          lastY = y;
        });
      }, { passive: true });
    }

    var x = $('[data-ksea-notice-close]', bar);
    if (x) x.addEventListener('click', function () { bar.hidden = true; measure(); });

    /* Copying the URL is the fastest reliable path out of a webview — far
       easier on a phone than finding the overflow menu. Only offered where
       the clipboard API actually exists and the page is secure. */
    var copy = $('[data-ksea-notice-copy]', bar);
    if (copy) {
      if (!navigator.clipboard) { copy.hidden = true; return; }
      copy.addEventListener('click', function () {
        navigator.clipboard.writeText(window.location.href).then(function () {
          var was = copy.textContent;
          copy.textContent = 'Link copied — paste it in Safari';
          setTimeout(function () { copy.textContent = was; }, 2600);
        }, function () { /* clipboard refused; the written instructions stand */ });
      });
    }
  }

  /* ══ 2. COUNTDOWN ═════════════════════════════════════════════════════
     Three states, all built, because nobody is editing code on launch day:

       pre   now  <  early access opens   → counts down to the opening
       ea    open <= now < public release → counts down to the end of 20% off
       live  now  >= public release       → "It's live", buy link

     Server-rendered default is `pre`, which is both the correct state for the
     whole of the funnel's life and the safe thing to show if JS never runs.

     Times are absolute instants (the ISO strings carry +01:00), so the clock
     is correct for a viewer in Los Angeles, Tokyo or Perth without any
     timezone handling here. */

  function initCountdown() {
    var roots = $$('[data-ksea-countdown]');
    if (!roots.length) return;

    var parsed = roots.map(function (root) {
      return {
        root:  root,
        start: parseISO(root.getAttribute('data-ea-start')),
        end:   parseISO(root.getAttribute('data-public'))
      };
    }).filter(function (c) { return c.start && c.end; });

    if (!parsed.length) return;

    function show(root, sel, state) {
      $$(sel, root).forEach(function (el) {
        el.hidden = el.getAttribute(sel.replace(/[\[\]]/g, '').split('=')[0]) !== state;
      });
    }

    function setState(root, state) {
      $$('[data-cd-state]', root).forEach(function (el) {
        el.hidden = el.getAttribute('data-cd-state') !== state;
      });
      /* CTA groups can live outside the countdown element (the hero's buttons
         sit below the clock), so they are addressed at document level and
         keyed to the same state. */
      $$('[data-cta-state]').forEach(function (el) {
        el.hidden = el.getAttribute('data-cta-state') !== state;
      });
      document.documentElement.setAttribute('data-ksea-phase', state);
    }

    function paint(root, ms) {
      var t = Math.max(0, ms);
      var s = Math.floor(t / 1000);
      var d = Math.floor(s / 86400);
      var h = Math.floor((s % 86400) / 3600);
      var m = Math.floor((s % 3600) / 60);
      var ss = s % 60;
      var map = { d: d, h: h, m: m, s: ss };
      $$('[data-cd]', root).forEach(function (el) {
        var k = el.getAttribute('data-cd');
        if (!(k in map)) return;
        var v = k === 'd' ? String(map[k]) : pad(map[k]);
        if (el.textContent !== v) el.textContent = v;
      });
    }

    /* THE MANUAL SWITCH.
       Any section can carry data-force="pre|ea|live"; "auto" means the clock
       decides. The first forced value found wins for the WHOLE page, so Jack
       flips one switch on the hero and the closing CTA follows — rather than
       setting it in two places and risking a page that contradicts itself at
       6pm from a hotel in Italy.

       The forced state is already rendered server-side, so this only keeps the
       clock ticking; nothing moves on screen when the script catches up. */
    var forced = null;
    for (var i = 0; i < parsed.length; i++) {
      var f = parsed[i].root.getAttribute('data-force');
      if (f && f !== 'auto') { forced = f; break; }
    }

    function tick() {
      var now = Date.now();
      parsed.forEach(function (c) {
        var state = forced || (now < c.start.getTime() ? 'pre'
                            :  now < c.end.getTime()   ? 'ea'
                            :  'live');
        if (state !== c.state) { c.state = state; setState(c.root, state); }
        /* Still counts down while forced: forcing "ea" early should show the
           real time left on the discount, not a frozen clock. */
        if (state === 'pre')  paint(c.root, c.start.getTime() - now);
        if (state === 'ea')   paint(c.root, c.end.getTime()   - now);
      });
    }

    tick();
    setInterval(tick, 1000);
  }

  /* ══ 3. THE REMINDER BUTTON (.ics) ════════════════════════════════════
     Generated client-side so there is no server, no app, and no third party
     between the tap and the calendar entry.

     RFC 5545 details that matter in practice: TEXT values must escape
     backslash, semicolon, comma and newline, and lines longer than 75 octets
     must be folded with CRLF + a single leading space. Apple Calendar is
     forgiving about both; Outlook is not, and a malformed file fails
     silently, which is the worst possible failure for this button. */

  function icsEscape(s) {
    return String(s)
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r?\n/g, '\\n');
  }

  /* Fold on octets, not characters — an em dash is three bytes in UTF-8 and
     folding by character length overruns the limit on lines full of them. */
  function icsFold(line) {
    var bytes = 0, out = '', chunk = '';
    for (var i = 0; i < line.length; i++) {
      var ch = line[i];
      var n = encodeURIComponent(ch).replace(/%[0-9A-F]{2}/g, 'x').length;
      if (bytes + n > 73) { out += (out ? '\r\n ' : '') + chunk; chunk = ''; bytes = 1; }
      chunk += ch; bytes += n;
    }
    return out + (out ? '\r\n ' : '') + chunk;
  }

  function icsDate(d) {
    return d.getUTCFullYear() + pad(d.getUTCMonth() + 1) + pad(d.getUTCDate()) + 'T' +
           pad(d.getUTCHours()) + pad(d.getUTCMinutes()) + pad(d.getUTCSeconds()) + 'Z';
  }

  function buildICS(o) {
    var lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Roughcut//Keystone Jacket//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      'UID:' + o.uid,
      'DTSTAMP:' + icsDate(new Date()),
      'DTSTART:' + icsDate(o.start),
      'DTEND:' + icsDate(o.end),
      'SUMMARY:' + icsEscape(o.title),
      'DESCRIPTION:' + icsEscape(o.desc),
      'URL:' + icsEscape(o.url),
      'LOCATION:' + icsEscape(o.url),
      'BEGIN:VALARM',
      'TRIGGER:-PT15M',
      'ACTION:DISPLAY',
      'DESCRIPTION:' + icsEscape(o.alarm),
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR'
    ];
    return lines.map(icsFold).join('\r\n') + '\r\n';
  }

  function gcalURL(o) {
    return 'https://calendar.google.com/calendar/render?action=TEMPLATE' +
      '&text='    + encodeURIComponent(o.title) +
      '&dates='   + icsDate(o.start) + '/' + icsDate(o.end) +
      '&details=' + encodeURIComponent(o.desc) +
      '&location='+ encodeURIComponent(o.url);
  }

  function initReminder() {
    var btns = $$('[data-ksea-ics]');
    var links = $$('[data-ksea-gcal]');
    if (!btns.length && !links.length) return;

    function opts(el) {
      var start = parseISO(el.getAttribute('data-start'));
      if (!start) return null;
      var end = parseISO(el.getAttribute('data-end')) || new Date(start.getTime() + 3600000);
      return {
        start: start,
        end:   end,
        title: el.getAttribute('data-title') || 'Keystone Jacket — Early Access opens',
        desc:  el.getAttribute('data-desc')  || '',
        alarm: el.getAttribute('data-alarm') || 'Keystone early access opens in 15 minutes',
        url:   el.getAttribute('data-url')   || window.location.origin,
        file:  el.getAttribute('data-filename') || 'keystone-early-access.ics'
      };
    }

    /* The Google Calendar href is written at load, never on click, so the
       link is a real link: long-press, open-in-new-tab and copy-link all
       behave the way people expect. It is always visible — not gated behind
       in-app detection — because detection can be wrong and this is the
       fallback that always works. */
    links.forEach(function (a) {
      var o = opts(a);
      if (o) a.href = gcalURL(o);
    });

    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var o = opts(btn);
        if (!o) return;

        var ics = buildICS({
          uid: 'keystone-ea-' + o.start.getTime() + '-' + Date.now() + '@roughcutpatterns.com',
          start: o.start, end: o.end, title: o.title,
          desc: o.desc, alarm: o.alarm, url: o.url
        });

        try {
          var blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
          var url  = URL.createObjectURL(blob);
          var a    = document.createElement('a');
          a.href = url;
          a.download = o.file;
          a.rel = 'noopener';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
        } catch (e) {
          /* If Blob or the object URL is unavailable there is nothing useful
             to retry — the visible Google Calendar link is the answer. */
        }

        var label = $('[data-ksea-ics-label]', btn) || btn;
        var was = label.textContent;
        label.textContent = 'Reminder saved';
        btn.classList.add('is-done');
        setTimeout(function () {
          label.textContent = was;
          btn.classList.remove('is-done');
        }, 3200);

        /* Inside Instagram the download very often does nothing at all and
           gives no error. Surfacing the fallback at exactly the moment it
           failed is far more use than a warning nobody read on arrival. */
        if (document.documentElement.classList.contains('ksea-inapp')) {
          var hint = $('[data-ksea-ics-hint]', btn.closest('[data-ksea-cta]') || document);
          if (hint) hint.hidden = false;
        }
      });
    });
  }

  /* ══ 4. SIZE CHART ════════════════════════════════════════════════════
     Eight sizes × four measurements. All figures are FINISHED GARMENT in cm;
     the inch view is a display conversion (÷2.54, one decimal), never a
     second set of stored numbers, so the two can never drift apart.

     Both the chosen size and the chosen unit persist in localStorage. A
     significant share of this audience is US-based and will come back to this
     page more than once before launch; making them re-pick inches every visit
     is a small insult repeated often. Storage is wrapped because Safari
     private mode throws on write rather than failing quietly. */

  var SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];
  var MEAS = [
    { k: 'A', label: 'Sleeve length',                v: [56, 57.6, 59.2, 60.8, 62.4, 64, 65.6, 67.2] },
    { k: 'B', label: 'Chest width (pit to pit, flat)', v: [47.5, 51.5, 55.5, 59.5, 63.5, 67.5, 71.5, 75.5] },
    { k: 'C', label: 'Body length',                  v: [57.6, 59.5, 61.4, 63.3, 65.2, 67.1, 69, 70.9] },
    { k: 'D', label: 'Hem circumference',            v: [84.9, 92.9, 100.9, 108.9, 116.9, 124.9, 132.9, 140.9] }
  ];

  function store(key, val) {
    try {
      if (val === undefined) return window.localStorage.getItem(key);
      window.localStorage.setItem(key, val);
    } catch (e) { return null; }
  }

  function initSize() {
    var root = $('[data-ksea-size]');
    if (!root) return;

    var pills = $('[data-size-pills]', root);
    var out   = $('[data-size-out]', root);
    var units = $$('[data-unit]', root);
    if (!pills || !out) return;

    var unit = store('ksea_unit') === 'in' ? 'in' : 'cm';
    var idx  = parseInt(store('ksea_size'), 10);
    if (isNaN(idx) || idx < 0 || idx >= SIZES.length) idx = 3;   /* default M */

    SIZES.forEach(function (s, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.textContent = s;
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-controls', out.id || '');
      b.addEventListener('click', function () { idx = i; store('ksea_size', i); render(); });
      pills.appendChild(b);
    });

    units.forEach(function (b) {
      b.addEventListener('click', function () {
        unit = b.getAttribute('data-unit');
        store('ksea_unit', unit);
        render();
      });
    });

    function fmt(cm) {
      return unit === 'cm'
        ? num(cm) + ' cm'
        : (Math.round((cm / 2.54) * 10) / 10).toFixed(1) + ' in';
    }

    function render() {
      Array.prototype.forEach.call(pills.children, function (b, i) {
        b.classList.toggle('is-on', i === idx);
        b.setAttribute('aria-selected', i === idx ? 'true' : 'false');
      });
      units.forEach(function (b) {
        var on = b.getAttribute('data-unit') === unit;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      var html = '<div class="ksea-size__head">Size ' + SIZES[idx] +
                 ' <span>finished garment</span></div>';
      MEAS.forEach(function (m) {
        html += '<div class="ksea-size__row">' +
                  '<span class="k" aria-hidden="true">' + m.k + '</span>' +
                  '<span class="l">' + m.label + '</span>' +
                  '<span class="v ksea-num">' + fmt(m.v[idx]) + '</span>' +
                '</div>';
      });
      out.innerHTML = html;

      /* The same four figures, painted onto the technical flat. The drawing
         is the theme-editor image; these are positioned HTML on top of it, so
         they stay crisp and nothing has to be redrawn to change a size. */
      /* Searched from the document, not from root: the widget lives inside
         the card and the flat sits in the column beside it, so they are
         siblings rather than parent and child. */
      MEAS.forEach(function (m) {
        $$('[data-flat-v="' + m.k + '"]').forEach(function (v) {
          v.textContent = fmt(m.v[idx]);
        });
      });
    }

    render();
    reveal(root);
  }

  /* ══ 5. FABRIC CALCULATOR ═════════════════════════════════════════════
     The single most useful thing on the page: the number somebody screenshots
     and carries into a fabric shop. Two tables, eleven widths.

     Every figure is measured at 3XL — the largest graded size — with a 15%
     margin already folded into the "buy" number. That framing has to travel
     with the number, which is why the note under the result is not optional
     copy: a bare "4.0 m" read by somebody cutting an XS is a different (and
     wrong) piece of advice from the same number read at 3XL.

     60cm in Full Modular is a genuine dead end rather than a small number —
     the Classic Yoke is wider than the fabric — so it gets its own state and
     an actual way out (build a single jacket instead), not an error. */

  var WIDTHS = [60, 70, 80, 90, 100, 110, 115, 120, 130, 140, 150];
  var SINGLE = { 60: [8.5, 7.3], 70: [7.5, 6.1], 80: [6.5, 5.5], 90: [6.0, 5.2], 100: [6.0, 4.8],
                 110: [5.5, 4.4], 115: [5.0, 4.0], 120: [4.5, 3.9], 130: [4.5, 3.7], 140: [4.0, 3.4], 150: [4.0, 3.2] };
  var FULL   = { 60: null,       70: [13.0, 11.1], 80: [11.5, 9.6], 90: [10.5, 8.8], 100: [9.5, 7.9],
                 110: [9.0, 7.4], 115: [8.0, 6.7], 120: [8.0, 6.6], 130: [7.5, 6.2], 140: [7.0, 5.8], 150: [6.5, 5.4] };

  var WIDTH_HINT = { 60: 'narrow selvedge', 76: '30" selvedge', 70: 'typical selvedge', 150: 'typical wide denim' };

  function initFabric() {
    var root = $('[data-ksea-fabric]');
    if (!root) return;

    var chips = $('[data-fab-chips]', root);
    var sel   = $('[data-fab-width]', root);          /* older markup, kept as a fallback */
    var out   = $('[data-fab-out]', root);
    var note  = $('[data-fab-note]', root);
    var roll  = $('[data-fab-roll]', root);
    var fill  = $('[data-fab-fill]', root);
    var modes = $$('[data-mode]', root);
    if (!out || (!chips && !sel)) return;

    var mode  = store('ksea_mode') === 'full' ? 'full' : 'single';
    var width = parseInt(store('ksea_width'), 10);

    /* A stored width from an older build of the page, or a width that is not
       one of the eleven, snaps to the nearest listed row rather than leaving
       the card with nothing selected. */
    if (isNaN(width)) width = 80;
    width = WIDTHS.reduce(function (a, b) {
      return Math.abs(b - width) < Math.abs(a - width) ? b : a;
    });

    /* Eleven chips instead of a <select>. A dropdown hides ten of the eleven
       answers behind a tap, and hides the fact that width is the thing
       driving the number. The short hint under a width is only printed where
       it says something a maker acts on. */
    var CHIP_HINT = { 60: 'narrow', 70: 'selvedge', 150: 'wide' };

    if (chips) {
      chips.innerHTML = WIDTHS.map(function (w) {
        return '<button type="button" data-w="' + w + '" aria-pressed="false">' +
               w + '<small>' + (CHIP_HINT[w] || 'cm') + '</small></button>';
      }).join('');
      chips.addEventListener('click', function (e) {
        var b = e.target.closest ? e.target.closest('button') : null;
        if (!b || !chips.contains(b)) return;
        width = parseInt(b.getAttribute('data-w'), 10);
        store('ksea_width', width);
        render();
      });
    } else {
      WIDTHS.forEach(function (w) {
        var o = document.createElement('option');
        o.value = w;
        o.textContent = w + ' cm' + (WIDTH_HINT[w] ? '  (' + WIDTH_HINT[w] + ')' : '');
        sel.appendChild(o);
      });
      sel.value = String(width);
      sel.addEventListener('change', function () {
        width = parseInt(sel.value, 10);
        store('ksea_width', width);
        render();
      });
    }

    modes.forEach(function (b) {
      b.addEventListener('click', function () {
        mode = b.getAttribute('data-mode');
        store('ksea_mode', mode);
        render();
      });
    });

    /* The metre figure counts up rather than cutting, because the point of
       the chips is watching the number move when the width changes. It is a
       transition on a number, not an entrance animation, so reduced-motion
       lands on the final value immediately instead of skipping a step. */
    var raf = null;
    function countTo(el, to) {
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var from = parseFloat(el.getAttribute('data-v') || '0');
      if (reduce || !window.requestAnimationFrame || from === to) {
        el.firstChild.nodeValue = num(to);
        el.setAttribute('data-v', to);
        return;
      }
      var t0 = 0;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function step(now) {
        if (!t0) t0 = now;
        var p = Math.min(1, (now - t0) / 520);
        var e = 1 - Math.pow(1 - p, 3);
        el.firstChild.nodeValue = num(from + (to - from) * e);
        if (p < 1) raf = requestAnimationFrame(step);
        else el.setAttribute('data-v', to);
      });
    }

    /* The bar is drawn against a fixed 0-15m scale rather than being
       normalised to the current answer, so 4.5m and 11.5m look like different
       amounts of cloth instead of two identical bars with different labels. */
    var SCALE = 15;

    function render() {
      modes.forEach(function (b) {
        var on = b.getAttribute('data-mode') === mode;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (chips) {
        $$('button', chips).forEach(function (b) {
          var on = parseInt(b.getAttribute('data-w'), 10) === width;
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      } else if (sel) {
        sel.value = String(width);
      }

      var row = (mode === 'single' ? SINGLE : FULL)[width];

      /* 60cm in Full Modular is a genuine dead end rather than a small
         number - the Classic Yoke is wider than the fabric - so it gets its
         own state and an actual way out, not an error. */
      if (!row) {
        out.innerHTML = '<div class="ksea-fab__bad">Not usable at ' + width + '&nbsp;cm</div>';
        if (note) {
          note.innerHTML =
            'The Classic Yoke is wider than ' + width + '&nbsp;cm, so you can\u2019t cut one of ' +
            'everything from a roll this narrow. A single jacket is fine \u2014 switch to ' +
            '<b>One jacket</b> above, or start from 70&nbsp;cm.';
        }
        if (roll) roll.style.opacity = '.25';
        if (fill) fill.style.width = '0%';
        return;
      }

      if (roll) roll.style.opacity = '1';

      var big = $('[data-fab-n]', out);
      if (!big) {
        out.innerHTML =
          '<div class="ksea-fab__big ksea-num"><span data-fab-n data-v="0">0</span> m</div>' +
          '<div class="ksea-fab__cap">Buy this much</div>';
        big = $('[data-fab-n]', out);
      }
      countTo(big, row[0]);
      if (fill) fill.style.width = Math.min(100, (row[0] / SCALE) * 100) + '%';

      if (note) {
        note.innerHTML =
          'Raw cut <b>' + num(row[1]) + '&nbsp;m</b>, with a <b>15% margin</b> already added. ' +
          'Measured at <b>3XL</b>, the largest size we grade \u2014 around <b>M\u2013L</b> expect about <b>' +
          num(row[0] * 0.85) + '&nbsp;m</b>, at <b>XXS\u2013XS</b> about <b>' +
          num(row[0] * 0.72) + '&nbsp;m</b>. Yokes and collars don\u2019t shrink as fast as fronts ' +
          'and backs, so treat the 3XL figure as the safe number. Always round up. ' +
          'Lining is a separate fabric and isn\u2019t included.';
      }
    }

    render();
    reveal(root);
  }

  /* ══ 6. PROGRESSIVE REVEAL ════════════════════════════════════════════
     Swap the static fallback table for the live widget, once — and only
     once — the widget has actually rendered a result. If anything above
     threw, the table stays and the numbers are still on the page. */

  function reveal(root) {
    var widget   = $('[data-ksea-widget]', root);
    var fallback = $('[data-ksea-fallback]', root);
    if (widget) widget.hidden = false;
    if (fallback) fallback.hidden = true;
  }

  /* ══ 7. SMOOTH ANCHOR TO THE CALCULATOR ═══════════════════════════════
     "Jump to fabric" is the hero's secondary action — it exists so somebody
     who only wants to buy cloth today can skip the whole page. The fixed site
     nav would otherwise cover the heading they land on. */

  function initAnchors() {
    $$('a[href^="#ksea-"]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        var t = document.getElementById(a.getAttribute('href').slice(1));
        if (!t) return;
        e.preventDefault();
        var top = t.getBoundingClientRect().top + window.pageYOffset - 76;
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: top, behavior: reduce ? 'auto' : 'smooth' });
        t.setAttribute('tabindex', '-1');
        t.focus({ preventScroll: true });
      });
    });
  }

  /* ══ 8. STATUS BAR COLOUR ═════════════════════════════════════════════
     theme.liquid publishes <meta name="theme-color" content="#2A2A30">, which
     paints the iOS status bar and Safari's chrome in the site's dark ink. A
     <meta> cannot read a CSS variable, so it cannot know this one page is
     white — the result is a black notch strip above a white page, which reads
     as a rendering fault rather than a choice. Corrected here rather than by
     adding a second meta tag, because browsers honour the first one. */

  function initThemeColor() {
    if (!document.documentElement.classList.contains('ksea-page')) return;
    var m = document.querySelector('meta[name="theme-color"]');
    if (m) m.setAttribute('content', '#ffffff');
  }

  /* ══ 8. THE SELVEDGE ══════════════════════════════════════════════════
     The red ID thread down the rail fills as you read, so the page tells you
     how far through you are without a scrollbar. Markup is server-rendered in
     ksea-hero.liquid, so the rail is already the right shape in the first
     frame and this only ever moves the thread.

     rAF-throttled: scroll fires far more often than the screen refreshes, and
     writing a style on every event is how a fixed element starts to judder. */

  function initSelvedge() {
    var fill = $('[data-ksea-fill]');
    var cap  = $('[data-ksea-cap]');
    if (!fill) return;

    var ticking = false;
    function paint() {
      ticking = false;
      var h = document.documentElement.scrollHeight - window.innerHeight;
      var p = h > 0 ? Math.min(1, Math.max(0, window.pageYOffset / h)) : 0;
      fill.style.height = (p * 100) + '%';
      if (cap) cap.style.top = (p * 100) + '%';
    }
    function request() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame ? window.requestAnimationFrame(paint) : paint();
    }
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    paint();
  }

  /* ══ 9. SCROLL REVEAL ═════════════════════════════════════════════════
     Fade and rise. No parallax anywhere on this page: nothing moves at a
     different speed from the page itself, so scrolling never feels like it is
     fighting you.

     Two rules keep it from ever costing somebody the content. The .ksea-rv
     class is added here, so with scripting off nothing is hidden in the first
     place. And anything already inside the first screen is revealed
     immediately rather than handed to the observer, so the opening frame -
     the one a link preview shows, and the one somebody sees before their
     thumb moves - is never blank. */

  /* The elements that get revealed. Written as a selector list here rather
     than as an attribute on every heading and card across fourteen section
     files: the markup stays clean, sections nobody is editing today get the
     animation for free, and there is one place to change it. An element whose
     ancestor is already revealing is dropped, so a card inside a revealing
     grid does not fade twice. */
  var RV = [
    '.ksea-sec > .ksea-wrap > .ksea-head',
    '.ksea-sec .ksea-card',
    '.ksea-dec__row',
    '.ksea-sec .ksea-callout',
    '.ksea-sec > .ksea-wrap > .ksea-grid > *',
    '.ksea-sec .ksea-flat',
    '.ksea-sec .ksea-faq__row',
    '.ksea-tut__head',
    '.ksea-tut__frame',
    '.ksea-tut__chapters li'
  ].join(',');

  function initReveal() {
    var els = $$('[data-ksea-rv]');

    if (!els.length) {
      var seen = [];
      $$(RV).forEach(function (e) {
        for (var i = 0; i < seen.length; i++) if (seen[i].contains(e)) return;
        seen.push(e);
      });
      seen.forEach(function (e, i) {
        e.setAttribute('data-ksea-rv', '');
        e.setAttribute('data-ksea-d', String((i % 3) + 1));
      });
      els = seen;
    }

    if (!els.length) return;

    if (!('IntersectionObserver' in window)) {
      els.forEach(function (e) { e.classList.add('is-in'); });
      return;
    }

    document.documentElement.classList.add('ksea-rv');

    var vh = window.innerHeight, live = [];
    els.forEach(function (e) {
      if (e.getBoundingClientRect().top < vh) e.classList.add('is-in');
      else live.push(e);
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        io.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });

    live.forEach(function (e) { io.observe(e); });
  }

  /* ── boot ────────────────────────────────────────────────────────────── */

  function boot() {
    try { initThemeColor(); } catch (e) {}
    try { initNotice();    } catch (e) {}
    try { initCountdown(); } catch (e) {}
    try { initReminder();  } catch (e) {}
    try { initSize();      } catch (e) {}
    try { initFabric();    } catch (e) {}
    try { initAnchors();   } catch (e) {}
    try { initSelvedge();  } catch (e) {}
    try { initReveal();    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
